import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// Paiement en ligne des inscriptions à un événement.
//
// Le visiteur peut :
//   • réserver en réglant sur place (aucun paiement en ligne) ;
//   • ou payer immédiatement par Mobile Money, à condition que le
//     SuperAdmin ait autorisé CE commerce à encaisser en ligne.
//
// Toute la logique d'encaissement vit dans la fonction Backend
// `zifek-event-checkout` : aucune clé de paiement ne transite par le
// navigateur et une inscription n'est confirmée qu'après encaissement réel.
// ─────────────────────────────────────────────────────────────

export type EventPaymentProvider = 'feexpay' | 'paypal' | 'stripe' | 'wallet';

export interface EventPaymentAvailability {
  /** Le commerce peut encaisser en ligne. */
  online: boolean;
  /** Moyens de paiement réellement autorisés par la plateforme. */
  methods: EventPaymentProvider[];
  /** Raison lisible quand le paiement en ligne n'est pas possible. */
  reason: string;
}

const METHOD_LABELS: Record<string, string> = {
  feexpay: 'Mobile Money',
  paypal: 'PayPal',
  stripe: 'Carte bancaire',
  wallet: 'Portefeuille Zifek',
};

export function eventPaymentLabel(method: string): string {
  return METHOD_LABELS[method] || method;
}

export function eventPaymentIcon(method: string): string {
  if (method === 'feexpay') return 'ri-smartphone-line';
  if (method === 'paypal') return 'ri-paypal-line';
  if (method === 'stripe') return 'ri-bank-card-line';
  if (method === 'wallet') return 'ri-wallet-3-line';
  return 'ri-secure-payment-line';
}

/** Détermine si le commerce peut encaisser en ligne et par quels moyens. */
export async function getEventPaymentAvailability(merchantId: number | string | null): Promise<EventPaymentAvailability> {
  const id = Number(merchantId) || 0;
  if (!id) return { online: false, methods: [], reason: 'Commerce inconnu.' };

  try {
    const [settingsRes, accessRes, providerRes] = await Promise.all([
      supabase.from('platform_payment_settings').select('scope').eq('id', 1).maybeSingle(),
      supabase.from('merchant_payment_access').select('enabled, payment_methods').eq('commerce_id', id).maybeSingle(),
      supabase.from('payment_providers').select('provider, enabled').eq('provider', 'feexpay').maybeSingle(),
    ]);

    const scopeAll = settingsRes.data?.scope === 'all';
    const accessEnabled = accessRes.data?.enabled === true;
    const feexpayReady = providerRes.data ? providerRes.data.enabled !== false : false;

    if (!feexpayReady) {
      return { online: false, methods: [], reason: 'Le paiement en ligne n’est pas activé sur la plateforme.' };
    }
    if (!scopeAll && !accessEnabled) {
      return {
        online: false,
        methods: [],
        reason: 'Ce commerce n’est pas encore autorisé à encaisser en ligne. Réservez et réglez sur place.',
      };
    }

    let methods: EventPaymentProvider[] = ['feexpay'];
    const raw = accessRes.data?.payment_methods;
    if (Array.isArray(raw)) {
      const allowed = raw.map((m) => String(m)).filter((m): m is EventPaymentProvider =>
        m === 'feexpay' || m === 'paypal' || m === 'stripe' || m === 'wallet');
      if (allowed.length > 0) methods = allowed;
    }

    return { online: methods.length > 0, methods, reason: '' };
  } catch {
    return { online: false, methods: [], reason: 'Vérification du paiement indisponible.' };
  }
}

export interface EventCheckoutInput {
  eventId: number;
  fullname: string;
  email?: string;
  phone?: string;
  seats: number;
  note?: string;
  payOnline: boolean;
  network?: string;
  country?: string;
  phoneNumber?: string;
}

export interface EventCheckoutResult {
  ok: boolean;
  /** Inscription enregistrée sans paiement en ligne. */
  offline?: boolean;
  /** Paiement gratuit (événement sans prix). */
  free?: boolean;
  /** Demande de paiement envoyée sur le téléphone. */
  pending?: boolean;
  registrationId?: number;
  reference?: string;
  amount?: number;
  currency?: string;
  instructions?: string;
  error?: string;
}

/** Lance l'inscription (et le cas échéant le paiement) côté Backend. */
export async function checkoutEventRegistration(input: EventCheckoutInput): Promise<EventCheckoutResult> {
  const { data, error } = await supabase.functions.invoke('zifek-event-checkout', {
    body: input,
  });
  if (error) {
    // La fonction renvoie un message métier explicite dans `error.context`.
    const context = (error as unknown as { context?: { error?: string } }).context;
    return { ok: false, error: context?.error || error.message || "L'inscription n'a pas pu aboutir." };
  }
  const result = (data || {}) as EventCheckoutResult;
  return { ...result, ok: result.ok !== false && !result.error };
}